// src/pages/BetslipPage.jsx - With Accumulator Support and USD Display
import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useBetSlip } from '../context/BetSlipContext';
import { useAuth } from '../context/AuthContext';
import { WalletAPI } from '../services/api';
import { useBTCPrice } from '../hooks/useBTCPrice';
import Header from '../components/layout/Header';
import Footer from '../components/layout/Footer';
import { FiTrash2, FiX, FiCheck, FiAlertCircle, FiLoader, FiZap, FiDollarSign } from 'react-icons/fi';
import { FaBitcoin } from 'react-icons/fa';

const BetslipPage = () => {
  const { 
    bets, 
    removeFromBetSlip, 
    clearBetSlip, 
    totalOdds, 
    placeBet,
    placeMultipleSingles,
    isPlacing: contextIsPlacing,
    error: contextError,
    betMode,
    switchBetMode,
    getTotalStake
  } = useBetSlip();
  const { user } = useAuth();
  const navigate = useNavigate();
  
  // State for stakes - stored in BTC internally
  const [stakeBTC, setStakeBTC] = useState('');
  const [singlesStakesBTC, setSinglesStakesBTC] = useState({});
  const [potentialWin, setPotentialWin] = useState(0);
  const [totalSinglesStake, setTotalSinglesStake] = useState(0);
  const [totalSinglesWin, setTotalSinglesWin] = useState(0);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(false);
  const [balance, setBalance] = useState(null);
  const [loadingBalance, setLoadingBalance] = useState(true);
  const [isPlacing, setIsPlacing] = useState(false);
  const [placeResults, setPlaceResults] = useState([]);
  const [selectedMode, setSelectedMode] = useState('singles');
  const [displayCurrency, setDisplayCurrency] = useState('usd'); // 'usd' or 'btc'

  // Use BTC Price Hook
  const { 
    price: btcPrice, 
    formattedPrice, 
    btcToUsd, 
    usdToBtc 
  } = useBTCPrice(60000);

  // Load user balance
  useEffect(() => {
    if (user) {
      loadBalance();
    }
  }, [user]);

  const loadBalance = async () => {
    setLoadingBalance(true);
    try {
      const response = await WalletAPI.getBalance();
      if (response && response.success !== false) {
        setBalance(response);
      }
    } catch (err) {
      // console.error('Failed to load balance:', err);
    } finally {
      setLoadingBalance(false);
    }
  };

  // Initialize singles stakes
  useEffect(() => {
    if (bets.length > 0 && betMode === 'singles') {
      const initialStakes = {};
      bets.forEach(bet => {
        initialStakes[bet.id] = 0; // Start at 0
      });
      setSinglesStakesBTC(initialStakes);
    }
  }, [bets, betMode]);

  // Calculate accumulator potential win
  useEffect(() => {
    if (betMode === 'accumulator' && stakeBTC && parseFloat(stakeBTC) > 0) {
      const win = parseFloat(stakeBTC) * totalOdds;
      setPotentialWin(win);
    } else {
      setPotentialWin(0);
    }
  }, [stakeBTC, totalOdds, betMode]);

  // Calculate singles totals
  useEffect(() => {
    if (betMode === 'singles') {
      let totalStake = 0;
      let totalWin = 0;
      
      bets.forEach(bet => {
        const s = parseFloat(singlesStakesBTC[bet.id]) || 0;
        totalStake += s;
        totalWin += s * bet.odds;
      });
      
      setTotalSinglesStake(totalStake);
      setTotalSinglesWin(totalWin);
    }
  }, [singlesStakesBTC, bets, betMode]);

  // === Formatting Helpers ===
  const formatBTC = (amount) => {
    if (!amount || isNaN(amount)) return '0.00000000';
    return `${amount.toFixed(8)}`;
  };

  const formatBTCWithLabel = (amount) => {
    if (!amount || isNaN(amount)) return '0.00000000 BTC';
    return `${amount.toFixed(8)} BTC`;
  };

  const formatUSD = (btcAmount) => {
    if (!btcAmount || isNaN(btcAmount)) return '$0.00';
    const usdAmount = btcToUsd(btcAmount);
    return `$${usdAmount.toFixed(2)}`;
  };

  const getDisplayStake = (btcAmount) => {
    if (!btcAmount || isNaN(btcAmount)) {
      return displayCurrency === 'usd' ? '$0.00' : '0.00000000 BTC';
    }
    if (displayCurrency === 'usd') {
      return formatUSD(btcAmount);
    }
    return formatBTCWithLabel(btcAmount);
  };

  const getDisplayWin = (btcAmount) => {
    if (!btcAmount || isNaN(btcAmount)) {
      return displayCurrency === 'usd' ? '$0.00' : '0.00000000 BTC';
    }
    if (displayCurrency === 'usd') {
      return formatUSD(btcAmount);
    }
    return formatBTCWithLabel(btcAmount);
  };

  // Get the BTC value from user input based on display currency
  const getBTCFromInput = (value) => {
    if (!value || isNaN(value)) return 0;
    if (displayCurrency === 'usd') {
      return usdToBtc(parseFloat(value));
    }
    return parseFloat(value);
  };

  // Get the display value from BTC amount
  const getDisplayValueFromBTC = (btcAmount) => {
    if (!btcAmount || isNaN(btcAmount)) return '';
    if (displayCurrency === 'usd') {
      return btcToUsd(btcAmount).toFixed(2);
    }
    return btcAmount.toFixed(8);
  };

  // Toggle currency display
  const toggleCurrency = () => {
    // When toggling, convert the current stake values
    if (stakeBTC) {
      const currentBTC = parseFloat(stakeBTC);
      if (displayCurrency === 'usd') {
        // Switching from USD to BTC - show BTC value
        setStakeBTC(currentBTC.toString());
      } else {
        // Switching from BTC to USD - show USD value
        setStakeBTC(currentBTC.toString());
      }
    }
    setDisplayCurrency(prev => prev === 'usd' ? 'btc' : 'usd');
  };

  // Handle singles stake change - store in BTC
  const handleSinglesStakeChange = (betId, value) => {
    const btcValue = getBTCFromInput(value);
    setSinglesStakesBTC(prev => ({
      ...prev,
      [betId]: btcValue
    }));
  };

  // Handle accumulator stake change - store in BTC
  const handleAccumulatorStakeChange = (value) => {
    const btcValue = getBTCFromInput(value);
    setStakeBTC(btcValue.toString());
  };

  // Handle place accumulator bet
  const handlePlaceAccumulator = async () => {
    if (!user) {
      setError('Please login to place bets');
      setTimeout(() => navigate('/login'), 1500);
      return;
    }

    if (bets.length < 2) {
      setError('Accumulator requires at least 2 selections');
      return;
    }

    const stakeValue = parseFloat(stakeBTC);
    if (!stakeValue || stakeValue <= 0) {
      setError('Please enter a valid stake amount');
      return;
    }

    if (stakeValue < 0.000001) {
      setError('Minimum stake is 0.0001 BTC');
      return;
    }

    if (balance && stakeValue > (balance.available_balance || 0)) {
      setError(`Insufficient balance. Need ${formatUSD(stakeValue)}`);
      return;
    }

    setError(null);
    setIsPlacing(true);

    try {
      // Prepare accumulator bet data
      const selections = bets.map(bet => ({
        fixture_id: bet.matchId,
        bet_type: bet.bet_type || 'match_winner',
        selection: bet.selection.toLowerCase(),
        selection_name: bet.selection,
        odds: bet.odds
      }));

      const betData = {
        selections: selections,
        stake: stakeValue,
        total_odds: totalOdds,
        isAccumulator: true
      };

      const result = await placeBet(betData);
      
      if (result.success) {
        setSuccess(true);
        setError(null);
        loadBalance();
        setTimeout(() => {
          clearBetSlip();
          navigate('/bet-history');
        }, 3000);
      } else {
        setError(result.error || 'Failed to place accumulator');
      }
    } catch (err) {
      setError(err.message || 'An error occurred');
    } finally {
      setIsPlacing(false);
    }
  };

  // Handle place singles bets
  const handlePlaceSingles = async () => {
    if (!user) {
      setError('Please login to place bets');
      setTimeout(() => navigate('/login'), 1500);
      return;
    }

    if (bets.length === 0) {
      setError('Your betslip is empty');
      return;
    }

    // Check if all bets have valid stakes
    const invalidBets = bets.filter(bet => {
      const stake = parseFloat(singlesStakesBTC[bet.id]) || 0;
      return stake <= 0;
    });

    if (invalidBets.length > 0) {
      setError(`Please set a valid stake for all ${bets.length} bets`);
      return;
    }

    // Check minimum stake for each bet
    const belowMinBets = bets.filter(bet => {
      const stake = parseFloat(singlesStakesBTC[bet.id]) || 0;
      return stake < 0.0001;
    });

    if (belowMinBets.length > 0) {
      setError(`Some bets are below minimum stake (0.0001 BTC)`);
      return;
    }

    // Check total balance
    if (balance && totalSinglesStake > (balance.available_balance || 0)) {
      setError(`Insufficient balance. Need ${formatUSD(totalSinglesStake)}`);
      return;
    }

    setError(null);
    setIsPlacing(true);
    setPlaceResults([]);

    try {
      // Prepare all bets
      const betsData = bets.map(bet => ({
        fixture_id: bet.matchId || bet.fixture_id,
        bet_type: bet.bet_type || 'match_winner',
        selection: bet.selection.toLowerCase(),
        selection_name: bet.selection,
        odds: parseFloat(bet.odds),
        stake: parseFloat(singlesStakesBTC[bet.id]) || 0
      }));

      const result = await placeMultipleSingles(betsData);
      
      if (result.success) {
        setSuccess(true);
        setError(null);
        loadBalance();
        setTimeout(() => {
          clearBetSlip();
          navigate('/bet-history');
        }, 3000);
      } else {
        setError(result.error || 'Some bets failed');
        setPlaceResults(result.results || []);
      }
    } catch (err) {
      setError(err.message || 'An error occurred');
    } finally {
      setIsPlacing(false);
    }
  };

  const handlePlaceBet = () => {
    if (betMode === 'accumulator') {
      handlePlaceAccumulator();
    } else {
      handlePlaceSingles();
    }
  };

  // Quick stake amounts
  const quickStakesUSD = [10, 25, 50, 100, 250, 500];
  const quickStakesBTC = [0.001, 0.005, 0.01, 0.05, 0.1];

  // Get quick stakes based on display currency
  const getQuickStakes = () => {
    if (displayCurrency === 'usd') {
      return quickStakesUSD.map(usd => ({
        label: `$${usd}`,
        btcValue: usdToBtc(usd),
        displayValue: usd
      }));
    }
    return quickStakesBTC.map(btc => ({
      label: `${btc} BTC`,
      btcValue: btc,
      displayValue: btc
    }));
  };

  // Apply quick stake to accumulator
  const applyQuickStakeToAccumulator = (btcValue) => {
    setStakeBTC(btcValue.toString());
  };

  // Apply quick stake to all singles
  const applyQuickStakeToAllSingles = (btcValue) => {
    const newStakes = {};
    bets.forEach(bet => {
      newStakes[bet.id] = btcValue;
    });
    setSinglesStakesBTC(newStakes);
  };

  return (
    <div className="min-h-screen bg-deep-black pt-16">
      <Header />
      
      <div className="max-w-7xl mx-auto px-4 py-8">
        <div className="flex flex-col lg:flex-row gap-8">
          {/* Main Betslip Section */}
          <div className="flex-1">
            <div className="bg-gray-900 rounded-xl p-6">
              <div className="flex justify-between items-center mb-6 flex-wrap gap-3">
                <h1 className="text-2xl font-bold text-white">
                  Betslip ({bets.length})
                </h1>
                <div className="flex items-center gap-3 flex-wrap">
                  {/* Currency Toggle */}
                  <button
                    onClick={toggleCurrency}
                    className="flex items-center gap-2 px-3 py-1.5 bg-gray-800 hover:bg-gray-700 rounded-lg text-sm transition-colors"
                  >
                    {displayCurrency === 'usd' ? (
                      <>
                        <FiDollarSign className="w-4 h-4 text-green-400" />
                        <span className="text-white">USD</span>
                      </>
                    ) : (
                      <>
                        <FaBitcoin className="w-4 h-4 text-yellow-500" />
                        <span className="text-white">BTC</span>
                      </>
                    )}
                  </button>
                  
                  {bets.length > 0 && (
                    <button
                      onClick={clearBetSlip}
                      className="text-red-400 hover:text-red-300 text-sm flex items-center gap-1"
                    >
                      <FiTrash2 className="w-4 h-4" />
                      Clear All
                    </button>
                  )}
                  <Link
                    to="/sports"
                    className="text-gray-400 hover:text-white text-sm"
                  >
                    Add More Bets
                  </Link>
                </div>
              </div>

              {/* BTC Price Display */}
              {btcPrice && (
                <div className="bg-gray-800 rounded-lg p-2 mb-4 text-center">
                  <span className="text-gray-400 text-sm">
                    1 BTC = {formattedPrice}
                  </span>
                </div>
              )}

              {/* Mode Selection */}
              {bets.length >= 2 && (
                <div className="bg-gray-800 rounded-lg p-3 mb-4">
                  <div className="flex gap-2">
                    <button
                      onClick={() => {
                        switchBetMode('singles');
                        setSelectedMode('singles');
                      }}
                      className={`flex-1 py-2 rounded-lg text-sm font-medium transition ${
                        betMode === 'singles'
                          ? 'bg-blue-600 text-white'
                          : 'bg-gray-700 text-gray-400 hover:text-white'
                      }`}
                    >
                      📋 Singles ({bets.length})
                    </button>
                    <button
                      onClick={() => {
                        switchBetMode('accumulator');
                        setSelectedMode('accumulator');
                      }}
                      className={`flex-1 py-2 rounded-lg text-sm font-medium transition ${
                        betMode === 'accumulator'
                          ? 'bg-yellow-600 text-white'
                          : 'bg-gray-700 text-gray-400 hover:text-white'
                      }`}
                    >
                      <FiZap className="inline mr-1" />
                      Accumulator ({totalOdds.toFixed(2)}x)
                    </button>
                  </div>
                </div>
              )}

              {/* Balance Display - USD Primary */}
              {user && balance && (
                <div className="bg-gray-800 rounded-lg p-3 mb-4 flex justify-between items-center">
                  <span className="text-gray-400 text-sm">Available Balance</span>
                  <div className="text-right">
                    <span className={`font-bold ${
                      (balance.available_balance || 0) >= (betMode === 'accumulator' ? parseFloat(stakeBTC) || 0 : totalSinglesStake) 
                        ? 'text-green-400' 
                        : 'text-red-400'
                    }`}>
                      {displayCurrency === 'usd' 
                        ? formatUSD(balance.available_balance || 0)
                        : formatBTCWithLabel(balance.available_balance || 0)
                      }
                    </span>
                    <div className="text-xs text-gray-500">
                      {displayCurrency === 'usd' 
                        ? formatBTCWithLabel(balance.available_balance || 0)
                        : formatUSD(balance.available_balance || 0)
                      }
                    </div>
                  </div>
                </div>
              )}

              {!user && (
                <div className="bg-yellow-900/30 border border-yellow-700 rounded-lg p-4 mb-6">
                  <p className="text-yellow-400 text-sm flex items-center gap-2">
                    <FiAlertCircle className="w-5 h-5" />
                    Please login to place bets
                  </p>
                  <Link to="/login" className="text-blue-400 hover:text-blue-300 text-sm mt-2 inline-block">
                    Login Now →
                  </Link>
                </div>
              )}

              {bets.length === 0 ? (
                <div className="text-center py-12">
                  <div className="text-6xl mb-4">📋</div>
                  <h3 className="text-xl font-bold text-white mb-2">Your betslip is empty</h3>
                  <p className="text-gray-400 mb-6">
                    Add bets from the sports page to get started
                  </p>
                  <Link
                    to="/sports"
                    className="bg-blue-600 hover:bg-blue-700 text-white px-6 py-3 rounded-lg font-semibold inline-block"
                  >
                    Browse Sports
                  </Link>
                  <br />
                  <br />
                  <Link
                    to="/bet-history"
                    className="bg-green-600 hover:bg-blue-700 text-white px-6 py-3 rounded-lg font-semibold inline-block"
                  >
                    View Bet History
                  </Link>
                </div>
              ) : (
                <>
                  {/* Bet Items */}
                  <div className="space-y-4 mb-6 max-h-[400px] overflow-y-auto">
                    {bets.map((bet, index) => {
                      const stakeBTCValue = singlesStakesBTC[bet.id] || 0;
                      const winBTCValue = stakeBTCValue * bet.odds;
                      
                      return (
                        <div
                          key={bet.id}
                          className="bg-gray-800 rounded-lg p-4 hover:bg-gray-750 transition"
                        >
                          <div className="flex justify-between items-start">
                            <div className="flex-1">
                              <div className="flex items-center gap-2">
                                <span className="text-xs text-gray-500">#{index + 1}</span>
                                <div className="text-sm text-gray-400">{bet.match}</div>
                              </div>
                              <div className="text-white font-semibold">
                                {bet.selection}
                              </div>
                              <div className="text-sm text-yellow-400">
                                Odds: {bet.odds.toFixed(2)}x
                              </div>
                            </div>
                            <button
                              onClick={() => removeFromBetSlip(bet.id)}
                              className="text-gray-500 hover:text-red-400 transition p-1"
                            >
                              <FiX className="w-5 h-5" />
                            </button>
                          </div>
                          
                          {/* Singles Stake Input - Display in chosen currency, store in BTC */}
                          {betMode === 'singles' && (
                            <div className="mt-3">
                              <div className="flex items-center gap-2">
                                <input
                                  type="number"
                                  value={displayCurrency === 'usd' 
                                    ? (stakeBTCValue ? btcToUsd(stakeBTCValue).toFixed(2) : '')
                                    : (stakeBTCValue ? stakeBTCValue.toFixed(8) : '')
                                  }
                                  onChange={(e) => {
                                    const value = e.target.value;
                                    if (value === '') {
                                      handleSinglesStakeChange(bet.id, '');
                                      return;
                                    }
                                    const numValue = parseFloat(value);
                                    if (!isNaN(numValue)) {
                                      handleSinglesStakeChange(bet.id, numValue);
                                    }
                                  }}
                                  placeholder={displayCurrency === 'usd' ? 'Stake (USD)' : 'Stake (BTC)'}
                                  className="flex-1 bg-gray-700 text-white px-3 py-2 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 text-sm"
                                  step={displayCurrency === 'usd' ? '0.01' : '0.0001'}
                                  min={displayCurrency === 'usd' ? '1' : '0.0001'}
                                />
                                <span className="text-gray-400 text-sm">
                                  {displayCurrency === 'usd' ? 'USD' : 'BTC'}
                                </span>
                                {stakeBTCValue > 0 && (
                                  <span className="text-green-400 text-sm font-medium">
                                    Win: {getDisplayWin(winBTCValue)}
                                  </span>
                                )}
                              </div>
                            </div>
                          )}
                        </div>
                      );
                    })}
                  </div>

                  {/* Accumulator Stake Input - Display in chosen currency, store in BTC */}
                  {betMode === 'accumulator' && (
                    <div className="bg-gray-800 rounded-lg p-4 mb-6">
                      <div className="mb-3">
                        <label className="block text-gray-400 text-sm mb-2">
                          Accumulator Stake ({displayCurrency === 'usd' ? 'USD' : 'BTC'})
                        </label>
                        <div className="relative">
                          <input
                            type="number"
                            value={displayCurrency === 'usd' 
                              ? (stakeBTC ? btcToUsd(parseFloat(stakeBTC)).toFixed(2) : '')
                              : (stakeBTC ? parseFloat(stakeBTC).toFixed(8) : '')
                            }
                            onChange={(e) => {
                              const value = e.target.value;
                              if (value === '') {
                                setStakeBTC('');
                                return;
                              }
                              const numValue = parseFloat(value);
                              if (!isNaN(numValue)) {
                                if (displayCurrency === 'usd') {
                                  // Convert USD to BTC for storage
                                  const btcValue = usdToBtc(numValue);
                                  setStakeBTC(btcValue.toString());
                                } else {
                                  setStakeBTC(value);
                                }
                              }
                            }}
                            placeholder={displayCurrency === 'usd' ? 'Enter stake in USD' : 'Enter stake in BTC'}
                            className="w-full bg-gray-700 text-white px-4 py-3 rounded-lg focus:outline-none focus:ring-2 focus:ring-yellow-500"
                            step={displayCurrency === 'usd' ? '0.01' : '0.0001'}
                            min={displayCurrency === 'usd' ? '1' : '0.0001'}
                          />
                          <span className="absolute right-4 top-1/2 -translate-y-1/2 text-gray-400">
                            {displayCurrency === 'usd' ? 'USD' : 'BTC'}
                          </span>
                        </div>
                        {stakeBTC && parseFloat(stakeBTC) > 0 && (
                          <div className="text-xs text-gray-400 mt-1">
                            {displayCurrency === 'usd' 
                              ? `≈ ${formatBTCWithLabel(parseFloat(stakeBTC))}`
                              : `≈ ${formatUSD(parseFloat(stakeBTC))}`
                            }
                          </div>
                        )}
                      </div>

                      <div className="flex gap-2 flex-wrap">
                        {getQuickStakes().map((item) => (
                          <button
                            key={item.label}
                            onClick={() => applyQuickStakeToAccumulator(item.btcValue)}
                            className="bg-gray-700 hover:bg-gray-600 text-white px-3 py-1 rounded text-sm transition"
                          >
                            {item.label}
                          </button>
                        ))}
                      </div>

                      <div className="mt-4 pt-3 border-t border-gray-700">
                        <div className="flex justify-between items-center">
                          <span className="text-gray-400">Total Odds</span>
                          <span className="text-yellow-400 font-bold text-lg">
                            {totalOdds.toFixed(2)}x
                          </span>
                        </div>
                        <div className="flex justify-between items-center mt-2">
                          <span className="text-gray-400">Potential Win</span>
                          <span className="text-green-400 font-bold text-xl">
                            {potentialWin > 0 
                              ? getDisplayWin(potentialWin)
                              : (displayCurrency === 'usd' ? '$0.00' : '0.00000000 BTC')
                            }
                          </span>
                        </div>
                      </div>
                    </div>
                  )}

                  {/* Singles Summary - Display in chosen currency */}
                  {betMode === 'singles' && (
                    <div className="bg-gray-800 rounded-lg p-4 mb-6">
                      <div className="grid grid-cols-2 gap-4">
                        <div>
                          <div className="text-sm text-gray-400">Total Selections</div>
                          <div className="text-white font-bold text-lg">{bets.length}</div>
                        </div>
                        <div>
                          <div className="text-sm text-gray-400">Total Stake</div>
                          <div className="text-yellow-400 font-bold text-lg">
                            {getDisplayStake(totalSinglesStake)}
                          </div>
                        </div>
                        <div className="col-span-2">
                          <div className="text-sm text-gray-400">Total Potential Win</div>
                          <div className="text-green-400 font-bold text-2xl">
                            {getDisplayWin(totalSinglesWin)}
                          </div>
                        </div>
                      </div>
                    </div>
                  )}

                  {/* Quick Stake All (Singles) */}
                  {betMode === 'singles' && bets.length > 1 && (
                    <div className="bg-gray-800 rounded-lg p-3 mb-4">
                      <div className="text-sm text-gray-400 mb-2">
                        Quick Stake All ({displayCurrency === 'usd' ? 'USD' : 'BTC'})
                      </div>
                      <div className="flex gap-2 flex-wrap">
                        {getQuickStakes().map((item) => (
                          <button
                            key={item.label}
                            onClick={() => applyQuickStakeToAllSingles(item.btcValue)}
                            className="bg-gray-700 hover:bg-gray-600 text-white px-3 py-1 rounded text-sm transition"
                          >
                            {item.label}
                          </button>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* Place Bet Button */}
                  <div className="flex flex-col sm:flex-row gap-3">
                    <button
                      onClick={handlePlaceBet}
                      disabled={!user || isPlacing || bets.length === 0}
                      className={`flex-1 py-3 rounded-lg font-semibold text-white transition ${
                        !user || isPlacing || bets.length === 0
                          ? 'bg-gray-600 cursor-not-allowed'
                          : betMode === 'accumulator'
                          ? 'bg-gradient-to-r from-yellow-600 to-amber-600 hover:from-yellow-500 hover:to-amber-500'
                          : 'bg-gradient-to-r from-green-600 to-emerald-600 hover:from-green-500 hover:to-emerald-500'
                      }`}
                    >
                      {isPlacing ? (
                        <span className="flex items-center justify-center gap-2">
                          <FiLoader className="animate-spin w-5 h-5" />
                          Placing {betMode === 'accumulator' ? 'Accumulator' : `${bets.length} Bet(s)`}...
                        </span>
                      ) : (
                        betMode === 'accumulator' 
                          ? `Place Accumulator (${totalOdds.toFixed(2)}x)`
                          : `Place All ${bets.length} Singles`
                      )}
                    </button>
                    <Link
                      to="/sports"
                      className="px-6 py-3 bg-gray-700 hover:bg-gray-600 rounded-lg text-white font-semibold text-center transition"
                    >
                      Add More
                    </Link>
                  </div>

                  {/* Results */}
                  {placeResults.length > 0 && (
                    <div className="mt-4 bg-gray-800 rounded-lg p-4">
                      <div className="text-sm font-medium text-white mb-2">Results</div>
                      <div className="space-y-1">
                        {placeResults.map((result, index) => (
                          <div key={index} className={`text-sm ${result.success ? 'text-green-400' : 'text-red-400'}`}>
                            {result.success ? '✅' : '❌'} Bet {index + 1}: {result.success ? 'Placed' : result.error}
                          </div>
                        ))}
                      </div>
                    </div>
                  )}

                  {(error || contextError) && (
                    <div className="mt-4 bg-red-900/50 border border-red-700 rounded-lg p-3 flex items-start gap-2">
                      <FiAlertCircle className="w-5 h-5 text-red-400 flex-shrink-0 mt-0.5" />
                      <p className="text-red-200 text-sm">{error || contextError}</p>
                    </div>
                  )}

                  {success && (
                    <div className="mt-4 bg-green-900/50 border border-green-700 rounded-lg p-3 flex items-center gap-2">
                      <FiCheck className="w-5 h-5 text-green-400 flex-shrink-0" />
                      <p className="text-green-200 text-sm">
                        {betMode === 'accumulator' ? 'Accumulator' : 'All bets'} placed successfully!
                      </p>
                    </div>
                  )}
                </>
              )}
            </div>
          </div>

          {/* Sidebar */}
          <div className="lg:w-80">
            <div className="bg-gray-900 rounded-xl p-6 sticky top-20">
              <h3 className="text-white font-bold mb-4">Quick Info</h3>
              <div className="space-y-4">
                <div className="bg-gray-800 rounded-lg p-3">
                  <div className="text-sm text-gray-400">Mode</div>
                  <div className="text-white font-bold capitalize">{betMode}</div>
                </div>
                <div className="bg-gray-800 rounded-lg p-3">
                  <div className="text-sm text-gray-400">Selections</div>
                  <div className="text-white font-bold">{bets.length}</div>
                </div>
                {betMode === 'accumulator' && (
                  <div className="bg-gray-800 rounded-lg p-3">
                    <div className="text-sm text-gray-400">Combined Odds</div>
                    <div className="text-yellow-400 font-bold">{totalOdds.toFixed(2)}x</div>
                  </div>
                )}
               
                {betMode === 'accumulator' && (
                  <div className="bg-yellow-900/30 border border-yellow-700/30 rounded-lg p-3">
                    <p className="text-yellow-300 text-xs">
                      <FiZap className="inline mr-1" />
                      All {bets.length} selections must win for the accumulator to pay out!
                    </p>
                  </div>
                )}
                <div className="bg-blue-900/30 border border-blue-700/30 rounded-lg p-3">
                  <p className="text-blue-300 text-xs">
                    💡 {betMode === 'accumulator' 
                      ? 'Accumulator combines all selections into one bet. Higher risk, higher reward!'
                      : 'Singles are individual bets. Each bet is placed separately.'}
                  </p>
                </div>
                {btcPrice && (
                  <div className="bg-gray-800 rounded-lg p-3">
                    <div className="text-sm text-gray-400">BTC/USD Rate</div>
                    <div className="text-white font-bold">{formattedPrice}</div>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>

      <Footer />
    </div>
  );
};

export default BetslipPage;